import { useDisclosure } from '@heroui/react';
import { ReactNode, createContext, useEffect, useMemo } from 'react';
import { useQueue } from '@shared/util/hooks/useQueue';
import { Alert } from './Alert';
import { Confirm } from './Confirm';
import {
  AlertCreator,
  ConfirmCreator,
  ModalQueueItem,
  useAlertCreator,
  useConfirmCreator,
} from './useModalCreator';

interface ModalContextValue {
  alert?: AlertCreator;
  confirm?: ConfirmCreator;
}

export const ModalContext = createContext<ModalContextValue>({});

export function ModalProvider({ children }: { children: ReactNode }) {
  const queue = useQueue<ModalQueueItem>();
  const alert = useAlertCreator(queue);
  const confirm = useConfirmCreator(queue);
  const value = useMemo(() => ({ alert, confirm }), [alert, confirm]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {queue.current && <ModalRenderer key={queue.current.id} modal={queue.current} />}
    </ModalContext.Provider>
  );
}

function ModalRenderer({ modal }: { modal: ModalQueueItem }) {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();

  const { id, type, ...modalProps } = modal;

  async function handleClose() {
    // call the alerts onClose handler if provided by the caller
    await modalProps.onClose?.();
    // close the alert
    onClose();
  }

  useEffect(onOpen, [onOpen]);

  const Component = type === 'confirm' ? Confirm : Alert;

  return (
    <Component
      key={id}
      {...modalProps}
      isOpen={isOpen}
      onClose={handleClose}
      onOpenChange={onOpenChange}
    />
  );
}
